import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LevelPlayer from './LevelPlayer';
import {
  blankLevel, checkLevel, cleanLevel, computeLiquid, drawBackground, drawLiquid, drawTile, LIMITS, resize, setTile, THEMES, TILES,
  type Level, type Theme,
} from '../game/levels';
import { MAKE_COST } from '../lib/shop';
import { useServerDraft } from './useServerDraft';
import SendChecklist from './SendChecklist';

const BLANK_LEVEL = blankLevel();

// ─────────────────────────────────────────────────────────────────────────────
// The level maker: paint a 2D obstacle course tile by tile, pick the look and
// the rules, play it yourself, then send it to the teacher for the arcade.
// You have to beat your own level (as it is now) before you can send it, so
// every level in the arcade can be won. Your work-in-progress is kept in this
// browser, so leaving the page doesn't lose it.
// ─────────────────────────────────────────────────────────────────────────────

const DRAFT_KEY = 'level-maker-draft';
const GROUPS = ['Blocks', 'Moving', 'Danger', 'Liquids', 'Power-ups', 'Things', 'Creatures', 'Special'] as const;

type Draft = { title: string; level: Level };
function loadDraft(): Draft & { local: boolean } {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? 'null');
    const level = cleanLevel(d?.level);
    if (level) return { title: String(d.title ?? '').slice(0, 60), level, local: true };
  } catch { /* fresh start */ }
  return { title: '', level: blankLevel(), local: false };
}

/** A tile's little picture for the palette. */
const iconCache = new Map<string, string>();
function tileIcon(ch: string): string {
  let u = iconCache.get(ch);
  if (!u) {
    const c = document.createElement('canvas'); c.width = c.height = 64;
    drawTile(c.getContext('2d')!, ch, 4, 4, 56, 0.3);
    u = c.toDataURL(); iconCache.set(ch, u);
  }
  return u;
}

type Props = { balance: number; look: unknown; onPublish: (title: string, level: Level) => Promise<void> };

export default function LevelEditor({ balance, look, onPublish }: Props) {
  const first = useMemo(loadDraft, []);
  const [title, setTitle] = useState(first.title);
  const [level, setLevel] = useState<Level>(first.level);
  useServerDraft('level', title, level, BLANK_LEVEL, first.local, cleanLevel, (t, l) => { setTitle(t); setLevel(l); });
  const [tool, setTool] = useState('g');
  const [past, setPast] = useState<Level[]>([]);
  const [future, setFuture] = useState<Level[]>([]);
  const [testing, setTesting] = useState(false);
  const [beaten, setBeaten] = useState('');          // the level (as JSON) last beaten in a test
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const painting = useRef<{ before: Level; erase: boolean } | null>(null);
  const levelRef = useRef(level);
  levelRef.current = level;

  const TS = typeof window !== 'undefined' && window.innerWidth < 640 ? 24 : 30;
  const asJson = JSON.stringify(level);
  const problem = checkLevel(level);
  const ready = !problem && beaten === asJson;

  // Keep the draft in this browser.
  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ title, level })); } catch { /* fine */ }
  }, [title, level]);

  // Draw the grid.
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = level.w * TS, H = level.h * TS;
    c.width = W * dpr; c.height = H * dpr; c.style.width = `${W}px`; c.style.height = `${H}px`;
    const g = c.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawBackground(g, level.theme, W, H, 0, 0);
    g.strokeStyle = 'rgba(30,38,51,0.12)'; g.lineWidth = 1;
    for (let x = 0; x <= level.w; x++) { g.beginPath(); g.moveTo(x * TS + 0.5, 0); g.lineTo(x * TS + 0.5, H); g.stroke(); }
    for (let y = 0; y <= level.h; y++) { g.beginPath(); g.moveTo(0, y * TS + 0.5); g.lineTo(W, y * TS + 0.5); g.stroke(); }
    for (let y = 0; y < level.h; y++) for (let x = 0; x < level.w; x++) {
      const ch = level.tiles[y * level.w + x];
      if (ch !== '.' && ch !== 'l' && ch !== 'a') drawTile(g, ch, x * TS, y * TS, TS, 0.3);
    }
    drawLiquid(g, level, computeLiquid(level), TS, 0.3, 0, level.w - 1, 0, level.h - 1);
    if (hover) {
      g.fillStyle = tool === '.' ? 'rgba(255,80,80,0.3)' : 'rgba(255,255,255,0.35)';
      g.fillRect(hover.x * TS, hover.y * TS, TS, TS);
      g.strokeStyle = '#1e2633'; g.strokeRect(hover.x * TS + 0.5, hover.y * TS + 0.5, TS - 1, TS - 1);
    }
  }, [level, hover, tool, TS, testing]);   // testing: the grid is a new canvas after a test play

  const change = useCallback((next: Level, before?: Level) => {
    setPast(p => [...p.slice(-60), before ?? levelRef.current]);
    setFuture([]);
    setLevel(next);
  }, []);
  const undo = useCallback(() => {
    setPast(p => {
      if (!p.length) return p;
      setFuture(f => [levelRef.current, ...f]);
      setLevel(p[p.length - 1]);
      return p.slice(0, -1);
    });
  }, []);
  const redo = useCallback(() => {
    setFuture(f => {
      if (!f.length) return f;
      setPast(p => [...p, levelRef.current]);
      setLevel(f[0]);
      return f.slice(1);
    });
  }, []);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (testing || !(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
      e.preventDefault();
      if (e.shiftKey) redo(); else undo();
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [undo, redo, testing]);

  const cellOf = (e: React.PointerEvent) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: Math.floor((e.clientX - r.left) / TS), y: Math.floor((e.clientY - r.top) / TS) };
  };
  const paintAt = (x: number, y: number, erase: boolean) => {
    const ch = erase ? '.' : tool;
    if (levelRef.current.tiles[y * levelRef.current.w + x] === ch) return;
    setLevel(l => setTile(l, x, y, ch));
  };

  if (testing) return (
    <LevelPlayer level={level} look={look} title={title || 'My level'}
      onDone={r => { if (r.won) setBeaten(asJson); }}
      footer={beaten === asJson ? <p className="notice good">You beat your level, so you can send it in! 🎉</p> : <p className="small muted">Beat your own level to be able to send it in.</p>}
      onClose={() => setTesting(false)} />
  );

  const publish = async () => {
    if (!title.trim()) { setError('Give your game a name.'); return; }
    if (problem) { setError(problem); return; }
    if (!ready) { setError('Play your level and reach the flag first.'); return; }
    setBusy(true); setError('');
    try {
      await onPublish(title.trim(), level);
      setTitle(''); setLevel(blankLevel()); setPast([]); setFuture([]); setBeaten('');
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not send your game.'); }
    setBusy(false);
  };

  return (
    <div className="level-editor">
      <div className="panel level-settings">
        <label className="field">
          <span>Name</span>
          <input value={title} maxLength={60} onChange={e => setTitle(e.target.value)} placeholder="Cactus Canyon" />
        </label>
        <div className="level-options">
          <label>World
            <select value={level.theme} onChange={e => change({ ...level, theme: e.target.value as Theme })}>
              {(Object.keys(THEMES) as Theme[]).map(t => <option key={t} value={t}>{THEMES[t].name}</option>)}
            </select>
          </label>
          <label>Gravity
            <select value={level.gravity} onChange={e => change({ ...level, gravity: e.target.value as Level['gravity'] })}>
              <option value="normal">Normal</option>
              <option value="floaty">Floaty (moon jumps)</option>
            </select>
          </label>
          <label>Time limit
            <select value={level.time} onChange={e => change({ ...level, time: Number(e.target.value) })}>
              <option value={0}>None</option>
              {[30, 60, 90, 120, 180, 300].map(n => <option key={n} value={n}>{n < 120 ? `${n} seconds` : `${n / 60} minutes`}</option>)}
            </select>
          </label>
          <label className="check-inline">
            <input type="checkbox" checked={level.allCoins} onChange={e => change({ ...level, allCoins: e.target.checked })} />
            Collect every coin to win
          </label>
          <span className="level-size">
            Size {level.w}×{level.h}
            <button type="button" className="small" disabled={level.w <= LIMITS.minW} onClick={() => change(resize(level, level.w - 10, level.h))}>− wide</button>
            <button type="button" className="small" disabled={level.w >= LIMITS.maxW} onClick={() => change(resize(level, level.w + 10, level.h))}>+ wide</button>
            <button type="button" className="small" disabled={level.h <= LIMITS.minH} onClick={() => change(resize(level, level.w, level.h - 2))}>− tall</button>
            <button type="button" className="small" disabled={level.h >= LIMITS.maxH} onClick={() => change(resize(level, level.w, level.h + 2))}>+ tall</button>
          </span>
        </div>
      </div>

      <div className="level-palette" role="radiogroup" aria-label="What to place">
        <button type="button" role="radio" aria-checked={tool === '.'} className={`pal-tile ${tool === '.' ? 'on' : ''}`} onClick={() => setTool('.')} title="Eraser (or right-click)">
          <span className="pal-eraser" aria-hidden>🧽</span><span>Eraser</span>
        </button>
        {GROUPS.map(gr => (
          <div key={gr} className="pal-group">
            <span className="pal-label">{gr}</span>
            {TILES.filter(t => t.group === gr).map(t => (
              <button key={t.ch} type="button" role="radio" aria-checked={tool === t.ch} className={`pal-tile ${tool === t.ch ? 'on' : ''}`} onClick={() => setTool(t.ch)} title={t.tip}>
                <img src={tileIcon(t.ch)} alt="" /><span>{t.name}</span>
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="level-toolbar row">
        <button type="button" className="small" disabled={!past.length} onClick={undo} title="Undo (Ctrl+Z)">↶ Undo</button>
        <button type="button" className="small" disabled={!future.length} onClick={redo} title="Redo (Ctrl+Shift+Z)">↷ Redo</button>
        <button type="button" className="small" onClick={() => { if (confirm('Start over with an empty level?')) change(blankLevel()); }}>🗑 Start over</button>
        <span className="spacer" />
        <span className="small muted">{TILES.find(t => t.ch === tool)?.tip ?? 'Click or drag to erase'}</span>
      </div>

      <div className="level-grid-wrap">
        <canvas ref={canvasRef} className="level-grid"
          onPointerDown={e => {
            e.preventDefault();
            canvasRef.current!.setPointerCapture(e.pointerId);
            const erase = e.button === 2 || tool === '.';
            painting.current = { before: levelRef.current, erase };
            const { x, y } = cellOf(e); paintAt(x, y, erase);
          }}
          onPointerMove={e => {
            const cell = cellOf(e);
            setHover(h => (h && h.x === cell.x && h.y === cell.y ? h : cell));
            if (painting.current && tool !== 'S' && tool !== 'G') paintAt(cell.x, cell.y, painting.current.erase);
          }}
          onPointerUp={() => {
            const p = painting.current; painting.current = null;
            if (p && p.before !== levelRef.current) change(levelRef.current, p.before);
          }}
          onPointerLeave={() => setHover(null)}
          onContextMenu={e => e.preventDefault()} />
      </div>

      {problem && <div className="notice">{problem}</div>}
      {error && <div className="notice bad" role="alert">{error}</div>}

      <div className="panel level-publish">
        <div className="row maker-actions">
          <SendChecklist named={!!title.trim()} problem={problem} beaten={beaten === asJson} balance={balance} verb="beat" />
          <button type="button" disabled={!!problem} onClick={() => { setError(''); setTesting(true); }}>▶ Play it</button>
          <button type="button" className="primary" disabled={busy || !ready || !title.trim() || balance < MAKE_COST} onClick={publish}>
            {busy ? 'Sending…' : `Send to my teacher · 🪙 ${MAKE_COST}`}
          </button>
        </div>
        <p className="small muted">Your teacher checks every game and sets its price to play. The 🪙 {MAKE_COST} isn't given back, even if your teacher says no. Your level is saved as you work, and your teacher can see it.</p>
      </div>
    </div>
  );
}
